import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { PublicHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { TIDS } from "@/lib/testIds";
import { CheckCircle2, Sparkles } from "lucide-react";

export default function PartnerSignup() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const refCode = (searchParams.get("ref") || "").trim().toUpperCase();
  const empCode = (searchParams.get("emp") || "").trim().toUpperCase();
  const [referrer, setReferrer] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    mobile: "",
    company_name: "",
    how_know_owners: "",
    payment_method: "ach",
    agree_terms: false,
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!refCode) return;
    apiClient
      .get(`/partners/lookup/${encodeURIComponent(refCode)}`)
      .then((r) => setReferrer(r.data))
      .catch(() => setReferrer(null));
  }, [refCode]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.agree_terms) {
      toast.error("Please agree to the terms to continue.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (refCode) payload.referred_by_code = refCode;
      if (empCode) payload.employee_code = empCode;
      await apiClient.post("/partners/signup", payload);
      toast.success("Welcome aboard! Use your mobile to log in.");
      setDone(true);
      setTimeout(() => navigate("/partner/login", { state: { mobile: form.mobile } }), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-up failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader />
      <div className="mx-auto max-w-6xl px-6 py-16 md:py-24 grid lg:grid-cols-12 gap-12">
        <aside className="lg:col-span-5">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Become a Partner</p>
          <h1 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-heading font-medium tracking-tight text-slate-900">
            Earn commissions referring business owners who need capital.
          </h1>
          <p className="mt-5 text-lg text-slate-600">
            We help business owners access funding. You bring the relationship, we handle the process, and you get paid when the deal closes.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-slate-700">
            {[
              "No experience or license required",
              "We handle underwriting and lender outreach",
              "Transparent commission tracking",
              "Paid on every funded deal",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <p className="mt-10 text-sm text-slate-500">
            Already a partner? <Link to="/partner/login" className="text-blue-600 hover:underline font-medium">Sign in</Link>
          </p>
        </aside>

        <div className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid={TIDS.signupForm}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7 md:p-9 space-y-5"
          >
            <h2 className="text-xl font-heading font-medium text-slate-900">Partner application</h2>
            <p className="text-sm text-slate-500 -mt-2">All fields required unless marked optional.</p>

            {referrer ? (
              <div
                data-testid={TIDS.signupReferredBy}
                className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-600 text-white shrink-0">
                  <Sparkles className="h-4 w-4" />
                </span>
                <div className="text-sm">
                  <div className="font-medium text-emerald-900">Referred by {referrer.full_name}</div>
                  <div className="text-emerald-700/80 text-xs">Code <code className="px-1 py-0.5 rounded bg-white/60 font-mono">{refCode}</code> applied to your application.</div>
                </div>
              </div>
            ) : null}

            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full name</Label>
                <Input
                  id="full_name"
                  data-testid={TIDS.signupFullName}
                  value={form.full_name}
                  onChange={(e) => update("full_name", e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid={TIDS.signupEmail}
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mobile">Mobile number</Label>
                <Input
                  id="mobile"
                  type="tel"
                  placeholder="+1 555 123 4567"
                  data-testid={TIDS.signupMobile}
                  value={form.mobile}
                  onChange={(e) => update("mobile", e.target.value)}
                  required
                />
                <p className="text-xs text-slate-500">Used for SMS login. Include country code.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_name">Company name <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input
                  id="company_name"
                  data-testid={TIDS.signupCompany}
                  value={form.company_name}
                  onChange={(e) => update("company_name", e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="how_know_owners">How do you know business owners?</Label>
              <Textarea
                id="how_know_owners"
                rows={3}
                data-testid={TIDS.signupHowKnow}
                placeholder="e.g. I run a CPA practice and work with 200+ small businesses..."
                value={form.how_know_owners}
                onChange={(e) => update("how_know_owners", e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Payment method preference</Label>
              <Select value={form.payment_method} onValueChange={(v) => update("payment_method", v)}>
                <SelectTrigger data-testid={TIDS.signupPaymentMethod}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ach">ACH (Direct Deposit)</SelectItem>
                  <SelectItem value="wire">Wire Transfer</SelectItem>
                  <SelectItem value="check">Mailed Check</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <label className="flex items-start gap-3 cursor-pointer pt-2">
              <Checkbox
                data-testid={TIDS.signupAgree}
                checked={form.agree_terms}
                onCheckedChange={(v) => update("agree_terms", !!v)}
                className="mt-1"
              />
              <span className="text-sm text-slate-600 leading-relaxed">
                I agree to the program terms and understand that commissions are paid only on successfully funded deals.
              </span>
            </label>

            <Button
              type="submit"
              disabled={loading || done}
              data-testid={TIDS.signupSubmit}
              className="w-full rounded-full bg-blue-600 hover:bg-blue-700 h-12 text-base shadow-sm hover:shadow-md transition-all"
            >
              {done ? "Welcome! Redirecting…" : loading ? "Submitting…" : "Submit application"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
